import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  LayoutGrid, Sun, Moon, ArrowLeft, Refrigerator, Trophy, 
  User, Users, Briefcase, HelpCircle, Award, Share2, Link2, Download
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { GoldMedalIcon } from '../components/MedalCelebration';
import { toast } from 'sonner';

const beneficiaryIcons = {
  me: User,
  work: Briefcase,
  others: Users,
  no_one: HelpCircle
};

const beneficiaryLabels = {
  me: 'Personal',
  work: 'Work',
  others: 'Others',
  no_one: 'No one'
};

const beneficiaryColors = {
  me: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  work: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  others: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  no_one: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
};

const medalStyles = {
  work: { label: 'Work Medal' },
  personal: { label: 'Personal Medal' },
  altruism: { label: 'Altruism Medal' },
};

export const AchievementsPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [medals, setMedals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achRes, medalRes] = await Promise.all([
          api.get('/achievements'),
          api.get('/medals'),
        ]);
        setAchievements(achRes.data);
        setMedals(medalRes.data);
      } catch (err) {
        console.error('Failed to load achievements/medals:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const medalGroups = Object.values(
    medals.reduce((acc, m) => {
      const g = acc[m.medal_type] || { medal_type: m.medal_type, count: 0, latest: m, maxMilestone: 0 };
      g.count += 1;
      if (new Date(m.awarded_at) >= new Date(g.latest.awarded_at)) g.latest = m;
      if ((m.milestone || 0) > g.maxMilestone) g.maxMilestone = m.milestone || 0;
      acc[m.medal_type] = g;
      return acc;
    }, {})
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
              <Refrigerator className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope']">Achievement Store</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 max-w-5xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass border-white/20 dark:border-white/10">
              <CardContent className="pt-6 text-center">
                <Trophy className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-3xl font-bold">{achievements.length}</p>
                <p className="text-sm text-muted-foreground">Total Achievements</p>
              </CardContent>
            </Card>
            {Object.entries(beneficiaryLabels).map(([key, label]) => {
              const count = achievements.filter(a => a.beneficiary === key).length;
              const Icon = beneficiaryIcons[key];
              return (
                <Card key={key} className="glass border-white/20 dark:border-white/10 hidden md:block">
                  <CardContent className="pt-6 text-center">
                    <Icon className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground">{label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Medals Section */}
          {medals.length > 0 && (
            <Card className="glass border-white/20 dark:border-white/10" data-testid="medals-section">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Your Medals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {medalGroups.map((group, index) => {
                    const style = medalStyles[group.medal_type] || medalStyles.work;
                    const medal = group.latest;
                    const shareUrl = `${window.location.origin}/shared/medal/${medal.medal_id}`;
                    return (
                      <motion.div
                        key={group.medal_type}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex flex-col items-center p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors relative group"
                        data-testid={`medal-group-${group.medal_type}`}
                      >
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-background/80"
                              data-testid={`medal-share-${medal.medal_id}`}
                            >
                              <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3 space-y-2" align="end">
                            <p className="text-xs font-medium text-muted-foreground">Share this medal</p>
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('I just received a medal from Brioritize.com')}&url=${encodeURIComponent(shareUrl)}`, '_blank')}
                                className="w-8 h-8 rounded-full bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 flex items-center justify-center transition-colors"
                                title="Share on X"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                              </button>
                              <button
                                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                                className="w-8 h-8 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors"
                                title="Share on Facebook"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                              </button>
                              <button
                                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank')}
                                className="w-8 h-8 rounded-full bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 flex items-center justify-center transition-colors"
                                title="Share on LinkedIn"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                              </button>
                            </div>
                            <button
                              onClick={async () => {
                                try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); } catch { toast.error('Copy failed'); }
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs hover:bg-muted transition-colors"
                            >
                              <Link2 className="w-3.5 h-3.5" /> Copy link
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await api.get(`/medals/${medal.medal_id}/gif`, { responseType: 'blob' });
                                  const url = URL.createObjectURL(res.data);
                                  const a = document.createElement('a');
                                  a.href = url; a.download = `brioritize-medal-${medal.medal_type}.gif`; a.click();
                                  URL.revokeObjectURL(url);
                                  toast.success('GIF downloaded!');
                                } catch { toast.error('Download failed'); }
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs hover:bg-muted transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" /> Download GIF
                            </button>
                          </PopoverContent>
                        </Popover>
                        <div className="relative mb-2">
                          <GoldMedalIcon type={group.medal_type} size={72} />
                          <span
                            className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-background"
                            data-testid={`medal-count-${group.medal_type}`}
                          >
                            ×{group.count}
                          </span>
                        </div>
                        <p className="font-semibold text-sm">{style.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{group.count} medal{group.count !== 1 ? 's' : ''} earned</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Up to {group.maxMilestone} achievements</p>
                        <Badge variant="outline" className="mt-2 text-[10px]">
                          Latest {format(new Date(medal.awarded_at), 'MMM d, yyyy')}
                        </Badge>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Achievements List */}
          <Card className="glass border-white/20 dark:border-white/10">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Your Achievements</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : achievements.length === 0 ? (
                <div className="text-center py-12">
                  <Refrigerator className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No achievements yet.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Drag interests to the fridge to mark them as achieved!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {achievements.map((achievement, index) => {
                    const Icon = beneficiaryIcons[achievement.beneficiary];
                    return (
                      <motion.div
                        key={achievement.achievement_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors duration-200"
                        data-testid={`achievement-${achievement.achievement_id}`}
                      >
                        <div className={`p-2 rounded-lg ${beneficiaryColors[achievement.beneficiary]}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{achievement.title}</p>
                          {achievement.description && (
                            <p className="text-sm text-muted-foreground mt-0.5">{achievement.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(achievement.achieved_at), 'MMM d, yyyy')}
                            </Badge>
                            <Badge className={beneficiaryColors[achievement.beneficiary]}>
                              {beneficiaryLabels[achievement.beneficiary]}
                            </Badge>
                            {achievement.time_to_achieve_hours > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {achievement.time_to_achieve_hours < 24 
                                  ? `${Math.round(achievement.time_to_achieve_hours)}h to complete`
                                  : `${Math.round(achievement.time_to_achieve_hours / 24)}d to complete`
                                }
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};
